import React from 'react';
import { Button } from '@parrot-software/design-system';
import { LoaderCircle } from '@parrot-software/design-system/icons';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import type { EmployeeSaveAction } from '@personnel/presentation/types';

interface EmployeeFormFooterProps {
  saveAction: EmployeeSaveAction;
  isSubmitting: boolean;
  cancelPath: string;
}

/**
 * The two buttons that close the form.
 *
 * They sit outside the tabs and outside the form element on purpose: whoever is looking at the
 * notifications has to be able to save without going back to the general tab. The save button is
 * still a submit of the form, through its form attribute.
 *
 * On a phone they stack with the save on top, because the reversed order puts the action that closes
 * the screen where the thumb already is.
 *
 * @param {EmployeeFormFooterProps} props - Component properties
 */
export const EmployeeFormFooter = ({
  saveAction,
  isSubmitting,
  cancelPath,
}: EmployeeFormFooterProps): React.ReactElement => {
  const { t } = useTranslation('personnel-employees');
  const history = useHistory();

  return (
    <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
      <Button className="w-full sm:w-auto" onClick={() => history.push(cancelPath)} type="button" variant="outline">
        {t('form.footer.cancel')}
      </Button>
      <Button
        className="w-full sm:w-auto"
        data-testid="employee-form-save"
        disabled={!saveAction.isEnabled || isSubmitting}
        form="employee-form"
        type="submit"
        variant="default"
      >
        {isSubmitting && <LoaderCircle className="size-4 animate-spin" />}
        {saveAction.label}
      </Button>
    </div>
  );
};
